import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, Wifi, RefreshCw } from 'lucide-react';

export const GlobalOfflineIndicator: React.FC = () => {
  const [isOffline, setIsOffline] = useState<boolean>(() => !navigator.onLine);
  const [wasOffline, setWasOffline] = useState<boolean>(false);
  const [showRestored, setShowRestored] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(false);

  const checkConnectivity = useCallback(async () => {
    setIsChecking(true);
    try {
      // Quick fetch to verify actual internet reachability
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      
      const response = await fetch('https://www.google.com/favicon.ico', {
        method: 'HEAD',
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      setIsOffline(false);
      if (wasOffline) {
        setShowRestored(true);
        setTimeout(() => setShowRestored(false), 2800);
      }
    } catch {
      // If no-cors fetch fails or times out
      if (!navigator.onLine) {
        setIsOffline(true);
        setWasOffline(true);
      }
    } finally {
      setIsChecking(false);
    }
  }, [wasOffline]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      if (wasOffline) {
        setShowRestored(true);
        setTimeout(() => setShowRestored(false), 2800);
      }
    };

    const handleOffline = () => {
      setIsOffline(true);
      setWasOffline(true);
      setShowRestored(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial state check
    if (!navigator.onLine) {
      setIsOffline(true);
      setWasOffline(true);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);

  return (
    <aside aria-label="Network Status" className="fixed top-[calc(0.5rem+env(safe-area-inset-top,0px))] sm:top-4 inset-x-0 z-[999999] flex justify-center pointer-events-none px-4">
      <AnimatePresence mode="wait">
        {isOffline && (
          <motion.div
            key="offline-banner"
            initial={{ opacity: 0, y: -24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 28 }}
            role="status"
            aria-live="polite"
            className="pointer-events-auto inline-flex items-center gap-3 px-4 py-2 rounded-full bg-neutral-900/95 dark:bg-[#12131F]/95 text-white border border-amber-500/40 shadow-xl shadow-black/40 backdrop-blur-xl max-w-sm"
          >
            <div className="relative flex items-center justify-center">
              <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping" />
              <div className="relative p-1 rounded-full bg-amber-500/20 text-amber-400">
                <WifiOff size={14} />
              </div>
            </div>
            
            <span className="text-xs font-semibold tracking-tight text-neutral-100">
              You're Offline
            </span>

            <button
              type="button"
              onClick={checkConnectivity}
              disabled={isChecking}
              className="ml-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-white/10 hover:bg-white/20 active:scale-95 transition-all text-neutral-200 inline-flex items-center gap-1 border border-white/10"
              aria-label="Retry connection"
            >
              <RefreshCw size={11} className={isChecking ? 'animate-spin' : ''} />
              <span>{isChecking ? 'Checking' : 'Retry'}</span>
            </button>
          </motion.div>
        )}

        {showRestored && !isOffline && (
          <motion.div
            key="restored-banner"
            initial={{ opacity: 0, y: -24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 28 }}
            role="status"
            aria-live="polite"
            className="pointer-events-auto inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-emerald-950/95 text-emerald-100 border border-emerald-500/40 shadow-xl shadow-emerald-900/30 backdrop-blur-xl"
          >
            <div className="p-1 rounded-full bg-emerald-500/20 text-emerald-400">
              <Wifi size={14} />
            </div>
            <span className="text-xs font-semibold tracking-tight text-emerald-200">
              Back Online
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </aside>
  );
};
